/**
 * Device Movement Modal
 * Modal สำหรับย้ายอุปกรณ์ของสัญญาเช่าไปโครงการอื่น (บริษัทเดียวกัน)
 *
 * Phase 3: เลือกย้ายบางรายการได้ (checkbox ต่อแถว, default ติ๊กทั้งหมด = ย้ายทั้งสัญญา)
 * - ติ๊กทั้งหมด → ย้ายทั้งสัญญา (Phase 1 เดิม): เปลี่ยนโครงการของสัญญา + resolve ใบแจ้งยกเลิก
 * - ติ๊กบางส่วน → split: สร้างสัญญาเช่าใหม่ที่โครงการปลายทางด้วยรายการที่เลือก
 *   (สัญญาเดิมยังอยู่โครงการเดิม, ใบแจ้งยกเลิกไม่ถูก resolve, ปิดโครงการเดิมไม่ได้)
 * - ถ้าโครงการเดิมไม่เหลือการเช่า/สัญญาอื่น → เลือกปิดโครงการเดิมได้ (เฉพาะย้ายทั้งหมด)
 * - FB-2026-0016: ติ๊ก "มีการปรับราคาเช่า" (เฉพาะสิทธิ์ rentals.billing_manage) → ราคาเดิม/วันสิ้นสุด +
 *   ราคาใหม่/วันเริ่ม ต่อรายการที่ย้าย (MovementPriceChangeFields)
 */
import React, { useState, useEffect } from 'react';
import {
  RiMapPinLine,
  RiTruckLine,
  RiDeviceLine,
  RiSimCardLine,
  RiInformationLine,
  RiAlertLine,
} from 'react-icons/ri';
import Modal from '../ui/Modal';
import SearchableSelect from '../ui/SearchableSelect';
import deviceMovementService, {
  TransferContext,
} from '../../services/deviceMovementService';
import { useTransferRental } from '../../hooks/mutations/useDeviceMovementMutations';
import { showAlert } from '../../utils/alert';
import { showSuccess } from '../../utils/toast';
import { getApiErrorMessage } from '../../utils/mapFieldErrors';
import { getLocalDateString } from '../../utils/dateUtils';
import { useSubmitGuard } from '../../hooks/useSubmitGuard';
import { usePermission } from '../../hooks/usePermission';
import { BILLING_MANAGE_PERMISSION } from '../../services/rentalService';
import {
  PriceRowInput,
  defaultPriceDates,
  validatePriceRow,
} from '../../utils/movementPriceChange';
import MovementPriceChangeFields from './MovementPriceChangeFields';

interface DeviceMovementModalProps {
  isOpen: boolean;
  onClose: () => void;
  rentalId: number | null;
  onSuccess?: () => void;
}

const isInactiveSite = (status: string | null | undefined): boolean =>
  String(status || '').toLowerCase() === 'inactive';

const DeviceMovementModal: React.FC<DeviceMovementModalProps> = ({
  isOpen,
  onClose,
  rentalId,
  onSuccess,
}) => {
  // Context state
  const [context, setContext] = useState<TransferContext | null>(null);
  const [loadingContext, setLoadingContext] = useState(false);
  const [contextError, setContextError] = useState<string | null>(null);

  // Form state
  const [toSiteId, setToSiteId] = useState<number | ''>('');
  const [reason, setReason] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [deactivateOldSite, setDeactivateOldSite] = useState(false);
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  // Phase 3: รายการที่เลือกย้าย (default ติ๊กทั้งหมด = ย้ายทั้งสัญญา)
  const [selectedItemIds, setSelectedItemIds] = useState<Set<number>>(new Set());

  // FB-2026-0016 (มติ PO 3 พ.ย.): ปรับราคาเช่าพร้อมการย้าย — เฉพาะผู้มีสิทธิ์ rentals.billing_manage
  // (backend ตอบ 403 ถ้าส่งราคามาโดยไม่มีสิทธิ์ · คนไม่มีสิทธิ์ย้ายได้ตามเดิมโดยไม่เห็นช่องราคา)
  const { hasPermission } = usePermission();
  const canManageBilling = hasPermission(BILLING_MANAGE_PERMISSION);
  const [priceEnabled, setPriceEnabled] = useState(false);
  const [priceRows, setPriceRows] = useState<Record<number, PriceRowInput>>({});

  const transferMutation = useTransferRental();
  // BUG-FE-DBLSUBMIT pattern: disabled={loading} needs a re-render; guard synchronously
  const submitGuard = useSubmitGuard();

  // โหลด transfer-context เมื่อเปิด modal
  useEffect(() => {
    if (!isOpen || !rentalId) return;

    let cancelled = false;
    const loadContext = async () => {
      setLoadingContext(true);
      setContextError(null);
      setContext(null);
      try {
        const data = await deviceMovementService.getTransferContext(rentalId);
        if (cancelled) return;
        setContext(data);
        // Phase 3: default ติ๊กทุกรายการ = ย้ายทั้งสัญญา
        setSelectedItemIds(new Set(data.items.map((it) => it.rental_item_id)));
        // เข้าเงื่อนไขปิดโครงการเดิม → default ติ๊กให้เลย
        const canDeactivate =
          data.old_site.remaining_active_rentals === 0 &&
          data.old_site.active_contracts === 0;
        setDeactivateOldSite(canDeactivate);
      } catch (error) {
        if (cancelled) return;
        console.error('Error loading transfer context:', error);
        setContextError(getApiErrorMessage(error, 'ไม่สามารถโหลดข้อมูลสำหรับย้ายอุปกรณ์ได้'));
      } finally {
        if (!cancelled) setLoadingContext(false);
      }
    };

    loadContext();
    return () => {
      cancelled = true;
    };
  }, [isOpen, rentalId]);

  // Reset form เมื่อเปิด modal
  useEffect(() => {
    if (isOpen) {
      setToSiteId('');
      setReason('');
      setEffectiveDate(getLocalDateString());
      setNotes('');
      setErrors({});
      setPriceEnabled(false);
      setPriceRows({});
    }
  }, [isOpen]);

  // FB-2026-0016: แถวปรับราคาของรายการที่เลือก — ค่าเริ่มต้น เดิมจบ = วันที่มีผล − 1 · ใหม่เริ่ม = วันที่มีผล
  const priceRowFor = (itemId: number): PriceRowInput =>
    priceRows[itemId] ?? { newRate: '', ...defaultPriceDates(effectiveDate) };

  const updatePriceRow = (itemId: number, patch: Partial<PriceRowInput>) => {
    setPriceRows((prev) => ({ ...prev, [itemId]: { ...priceRowFor(itemId), ...patch } }));
    setErrors((prev) => (prev[`price_${itemId}`] ? { ...prev, [`price_${itemId}`]: '' } : prev));
  };

  // เปลี่ยนวันที่มีผล → แถวที่ผู้ใช้ยังไม่ได้แก้วันเอง เลื่อนค่าเริ่มต้นตาม
  const handleEffectiveDateChange = (value: string) => {
    const prevDefaults = defaultPriceDates(effectiveDate);
    const nextDefaults = defaultPriceDates(value);
    setPriceRows((prev) => {
      const next: Record<number, PriceRowInput> = {};
      Object.entries(prev).forEach(([k, row]) => {
        next[Number(k)] = {
          ...row,
          oldEnd: row.oldEnd === prevDefaults.oldEnd ? nextDefaults.oldEnd : row.oldEnd,
          newStart: row.newStart === prevDefaults.newStart ? nextDefaults.newStart : row.newStart,
        };
      });
      return next;
    });
    setEffectiveDate(value);
  };

  // Phase 3: สรุปการเลือก
  const totalItems = context?.items.length ?? 0;
  const selectedCount = selectedItemIds.size;
  const isPartialMove = totalItems > 0 && selectedCount > 0 && selectedCount < totalItems;

  const toggleItem = (id: number) => {
    setSelectedItemIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
    setErrors((prev) => (prev.items ? { ...prev, items: '' } : prev));
  };

  // ย้ายบางส่วน = สัญญาเดิม (พร้อมรายการที่เหลือ) ยังอยู่โครงการเดิม → ปิดโครงการเดิมไม่ได้
  const canDeactivateOldSite =
    !!context &&
    !isPartialMove &&
    context.old_site.remaining_active_rentals === 0 &&
    context.old_site.active_contracts === 0;

  const blockingCount = context
    ? context.old_site.remaining_active_rentals + context.old_site.active_contracts
    : 0;

  const selectedSite = context?.candidate_sites.find((s) => s.id === toSiteId);

  // ระบบบันทึกการย้าย "หลัง" เกิดขึ้นจริง — ย้อนหลังได้ แต่ห้ามเป็นวันในอนาคต (backend ปฏิเสธ 400 เช่นกัน)
  const todayStr = getLocalDateString();

  // ปรับราคาจริงก็ต่อเมื่อมีสิทธิ์ + ติ๊ก (กันกรณีสิทธิ์ถูกถอดระหว่างเปิดหน้าต่าง → ไม่ส่งราคา)
  const pricingActive = canManageBilling && priceEnabled;

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};
    if (!toSiteId) {
      newErrors.toSiteId = 'กรุณาเลือกโครงการปลายทาง';
    }
    if (!reason.trim()) {
      newErrors.reason = 'กรุณาระบุเหตุผลในการย้าย';
    }
    if (!effectiveDate) {
      newErrors.effectiveDate = 'กรุณาระบุวันที่มีผล';
    } else if (effectiveDate > todayStr) {
      newErrors.effectiveDate = 'วันที่มีผลต้องไม่เกินวันนี้ (ระบบบันทึกการย้ายหลังเกิดขึ้นจริง)';
    }
    if (totalItems > 0 && selectedCount === 0) {
      newErrors.items = 'กรุณาเลือกรายการที่จะย้ายอย่างน้อย 1 รายการ';
    }
    if (pricingActive && context) {
      context.items
        .filter((it) => selectedItemIds.has(it.rental_item_id))
        .forEach((it) => {
          const msg = validatePriceRow(
            priceRowFor(it.rental_item_id),
            context.rental.last_billed_period_end,
            it.last_price_change_start,
          );
          if (msg) newErrors[`price_${it.rental_item_id}`] = msg;
        });
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm() || !rentalId || !context) return;
    if (!submitGuard.begin()) return; // duplicate click — already saving

    try {
      const result = await transferMutation.mutateAsync({
        rentalId,
        data: {
          to_site_id: toSiteId as number,
          reason: reason.trim(),
          effective_date: effectiveDate,
          deactivate_old_site: canDeactivateOldSite ? deactivateOldSite : false,
          notes: notes.trim() || null,
          expected_from_site_id: context.rental.site_id,
          // Phase 3: ติ๊กบางส่วน → ส่ง item_ids (ติ๊กครบ = ย้ายทั้งสัญญา ไม่ต้องส่ง)
          ...(isPartialMove ? { item_ids: Array.from(selectedItemIds) } : {}),
          // FB-2026-0016: ส่งเฉพาะเมื่อมีสิทธิ์ + ติ๊กปรับราคา (ไม่ส่ง key เลยถ้าไม่ปรับ)
          ...(pricingActive
            ? {
                price_changes: context.items
                  .filter((it) => selectedItemIds.has(it.rental_item_id))
                  .map((it) => {
                    const row = priceRowFor(it.rental_item_id);
                    return {
                      rental_item_id: it.rental_item_id,
                      new_rate: row.newRate.trim().replace(/,/g, ''),
                      old_billing_end_date: row.oldEnd,
                      new_billing_start_date: row.newStart,
                    };
                  }),
              }
            : {}),
        },
      });
      if (result.is_partial && result.new_rental_number) {
        showAlert(
          'ย้ายบางรายการสำเร็จ',
          [
            `ระบบสร้างสัญญาเช่าใหม่ ${result.new_rental_number} ที่โครงการปลายทางด้วยรายการที่เลือก`,
            ...(result.warnings || []),
          ].join('\n'),
          result.warnings && result.warnings.length > 0 ? 'warning' : 'success'
        );
      } else if (result.warnings && result.warnings.length > 0) {
        showAlert('ย้ายสำเร็จ แต่มีข้อควรทราบ', result.warnings.join('\n'), 'warning');
      } else {
        showSuccess('ย้ายอุปกรณ์ไปโครงการใหม่สำเร็จ');
      }
      onSuccess?.();
      onClose();
    } catch {
      // toast error จัดการโดย mutation hook แล้ว — modal คงเปิดให้แก้ไขต่อ
    } finally {
      submitGuard.end();
    }
  };

  const loading = transferMutation.isPending;

  if (!rentalId) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="ย้ายอุปกรณ์ไปโครงการอื่น" size="xl">
      {loadingContext ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" aria-hidden="true"></div>
          <span className="ml-3 text-secondary-500" role="status">กำลังโหลดข้อมูล...</span>
        </div>
      ) : contextError ? (
        <div className="py-8 text-center" role="alert">
          <RiAlertLine className="w-10 h-10 text-danger-500 mx-auto mb-3" aria-hidden="true" />
          <p className="text-danger-600">{contextError}</p>
        </div>
      ) : context ? (
        <div className="space-y-6">
          {/* ข้อมูลสัญญาเช่า */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 pb-2 border-b border-secondary-200">
              <div className="w-8 h-8 bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-lg flex items-center justify-center shadow-sm">
                <RiTruckLine className="w-4 h-4 text-white" />
              </div>
              <h3 className="text-base font-semibold text-secondary-800">สัญญาเช่า</h3>
            </div>
            <div className="bg-secondary-50 rounded-lg p-4">
              <div className="bg-white border border-secondary-200 rounded-md p-3">
                <p className="font-medium text-secondary-900">{context.rental.rental_number}</p>
                <p className="text-sm text-secondary-500">
                  {context.rental.company_name && `บริษัท: ${context.rental.company_name}`}
                  {context.rental.site_name && ` | โครงการปัจจุบัน: ${context.rental.site_name}`}
                </p>
              </div>
            </div>
          </div>

          {/* banner: ใบแจ้งยกเลิกค้าง (ข้อความต่างกันตามย้ายทั้งหมด/บางส่วน) */}
          {context.rental.has_pending_cancellation_notice && (
            <div className="flex items-start gap-2 bg-info-50 border border-info-200 rounded-lg p-3">
              <RiInformationLine className="w-5 h-5 text-info-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-info-800">
                {isPartialMove ? (
                  <>
                    รายการเช่านี้มีใบแจ้งยกเลิกค้างอยู่ — การย้ายบางรายการจะ
                    <b>ไม่</b>ปิดใบแจ้งยกเลิก: สัญญาเดิม (พร้อมรายการที่เหลือ)
                    ยังเดินสู่การยกเลิกตามแผน ส่วนรายการที่เลือกจะแยกเป็นสัญญาใหม่สถานะใช้งาน
                  </>
                ) : (
                  <>
                    รายการเช่านี้มีใบแจ้งยกเลิกค้างอยู่ — การย้ายจะบันทึกเป็น
                    &quot;รักษาลูกค้าได้ (ย้ายโครงการ)&quot; อัตโนมัติ
                    และรายการเช่าจะกลับมาเป็นสถานะใช้งาน
                  </>
                )}
              </p>
            </div>
          )}

          {/* รายการอุปกรณ์ที่จะย้าย (Phase 3: เลือกได้ต่อแถว) */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 pb-2 border-b border-secondary-200">
              <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center shadow-sm">
                <RiDeviceLine className="w-4 h-4 text-white" />
              </div>
              <h3 className="text-base font-semibold text-secondary-800">
                อุปกรณ์ที่จะย้าย ({selectedCount}/{context.items.length} รายการ)
              </h3>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-xs text-info-700 mb-3">
                * ติ๊กเลือกรายการที่ต้องการย้าย — ติ๊กทั้งหมด = ย้ายทั้งสัญญา ·
                ติ๊กบางส่วน = แยกรายการที่เลือกเป็นสัญญาเช่าใหม่ที่โครงการปลายทาง
              </p>
              <div className="space-y-2 max-h-56 overflow-y-auto">
                {context.items.map((item) => {
                  const checked = selectedItemIds.has(item.rental_item_id);
                  return (
                    <label
                      key={item.rental_item_id}
                      className={`bg-white border rounded-md p-3 flex items-start gap-3 cursor-pointer ${
                        checked ? 'border-primary-300' : 'border-secondary-200 bg-secondary-50' /* S2481: เดิม opacity-70 → ตัวอักษรเทาเล็กคอนทราสต์ไม่ผ่าน WCAG AA */
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleItem(item.rental_item_id)}
                        className="w-4 h-4 mt-0.5 text-primary-600 focus:ring-primary-500 rounded flex-shrink-0"
                        disabled={loading}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-secondary-900 truncate">
                          {item.product_name || 'ไม่ระบุสินค้า'}
                        </p>
                        {item.is_software_only ? (
                          <p className="text-xs text-secondary-500">Software อย่างเดียว (ไม่มีเครื่อง)</p>
                        ) : (
                          <p className="text-xs text-secondary-500 truncate">
                            {item.device_name || '-'}
                            {item.device_serial && ` · S/N: ${item.device_serial}`}
                          </p>
                        )}
                      </div>
                      {item.sim_number && (
                        <span className="inline-flex items-center text-xs text-secondary-500 whitespace-nowrap flex-shrink-0">
                          <RiSimCardLine className="w-3.5 h-3.5 mr-1" />
                          {item.sim_number}
                        </span>
                      )}
                    </label>
                  );
                })}
                {context.items.length === 0 && (
                  <p className="text-sm text-secondary-500 text-center py-2">
                    ไม่มีรายการอุปกรณ์ในสัญญาเช่านี้
                  </p>
                )}
              </div>
              {/* อยู่บนกล่องพื้นฟ้า (#eff6ff) — danger-600 ได้ 4.43 · ต้อง 700 */}
              {errors.items && (
                <p role="alert" className="text-danger-700 text-xs mt-2">{errors.items}</p>
              )}
              {isPartialMove && (
                <div className="mt-3 flex items-start gap-2 bg-warning-50 border border-warning-200 rounded-md p-3">
                  <RiInformationLine className="w-5 h-5 text-warning-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-warning-800">
                    ย้าย {selectedCount} จาก {context.items.length} รายการ →
                    ระบบจะ<b>สร้างสัญญาเช่าใหม่</b>ที่โครงการปลายทางด้วยรายการที่เลือก
                    (สัญญาเดิมยังอยู่โครงการเดิมพร้อมรายการที่เหลือ)
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* ปลายทาง + รายละเอียด */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b border-secondary-200">
              <div className="w-8 h-8 bg-gradient-to-br from-success-500 to-success-600 rounded-lg flex items-center justify-center shadow-sm">
                <RiMapPinLine className="w-4 h-4 text-white" />
              </div>
              <h3 className="text-base font-semibold text-secondary-800">โครงการปลายทาง</h3>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                โครงการปลายทาง (บริษัทเดียวกัน) <span className="text-red-500">*</span>
              </label>
              <SearchableSelect
                options={context.candidate_sites.map((site) => ({
                  value: site.id,
                  label: isInactiveSite(site.status) ? `${site.name} (ไม่ใช้งาน)` : site.name,
                }))}
                value={toSiteId}
                onChange={(value) => {
                  setToSiteId(value as number);
                  if (errors.toSiteId) setErrors((prev) => ({ ...prev, toSiteId: '' }));
                }}
                placeholder="-- เลือกโครงการปลายทาง --"
                searchPlaceholder="ค้นหาโครงการ..."
                name="toSiteId"
                disabled={loading}
                emptyMessage={'ไม่มีโครงการอื่นในบริษัทนี้ให้ย้ายไป\nต้องสร้างโครงการใหม่ที่หน้าข้อมูลโครงการก่อน'}
                // KD3 S2487 (FLOW-MOVE-011): ใช้ error/label ของ SearchableSelect — ได้ role=alert + aria-invalid/
                // aria-describedby ที่ผูกกับ combobox · เดิม <p> แยกไม่ประกาศ และ combobox ชื่อเป็น placeholder
                label="โครงการปลายทาง"
                error={errors.toSiteId || undefined}
              />
              {selectedSite && isInactiveSite(selectedSite.status) && (
                <p className="text-xs text-warning-700 mt-1">
                  โครงการนี้อยู่ในสถานะไม่ใช้งาน — ระบบจะเปิดใช้งานให้อัตโนมัติเมื่อบันทึกการย้าย
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="movement-effective-date" className="block text-sm font-medium text-secondary-700 mb-1">
                  วันที่มีผล <span className="text-red-500">*</span>
                </label>
                <input
                  id="movement-effective-date"
                  type="date"
                  name="effectiveDate"
                  aria-required="true"
                  aria-invalid={errors.effectiveDate ? true : undefined}
                  aria-describedby="movement-effective-date-hint"
                  value={effectiveDate}
                  max={todayStr}
                  onChange={(e) => {
                    handleEffectiveDateChange(e.target.value);
                    if (errors.effectiveDate) setErrors((prev) => ({ ...prev, effectiveDate: '' }));
                  }}
                  className={`input${errors.effectiveDate ? ' border-red-300' : ''}`}
                  disabled={loading}
                />
                {errors.effectiveDate ? (
                  <p id="movement-effective-date-hint" role="alert" className="text-danger-600 text-xs mt-1">{errors.effectiveDate}</p>
                ) : (
                  <p id="movement-effective-date-hint" className="text-xs text-secondary-500 mt-1">
                    ระบุวันที่ย้ายจริง (ย้อนหลังได้ ไม่เกินวันนี้)
                  </p>
                )}
              </div>
            </div>

            {canManageBilling && (
              <MovementPriceChangeFields
                enabled={priceEnabled}
                onToggle={setPriceEnabled}
                items={context.items.filter((it) => selectedItemIds.has(it.rental_item_id))}
                rowFor={priceRowFor}
                onChange={updatePriceRow}
                errors={errors}
                lastBilledPeriodEnd={context.rental.last_billed_period_end}
                fromSiteName={context.rental.site_name}
                disabled={loading}
              />
            )}

            <div>
              <label htmlFor="movement-reason" className="block text-sm font-medium text-secondary-700 mb-1">
                เหตุผลในการย้าย <span className="text-red-500">*</span>
              </label>
              <textarea
                id="movement-reason"
                maxLength={2000}
                name="reason"
                aria-required="true"
                aria-invalid={errors.reason ? true : undefined}
                aria-describedby={errors.reason ? 'movement-reason-error' : undefined}
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  if (errors.reason) setErrors((prev) => ({ ...prev, reason: '' }));
                }}
                rows={3}
                className={`input${errors.reason ? ' border-red-300' : ''}`}
                placeholder="เช่น ลูกค้าย้ายไปใช้งานที่โครงการใหม่..."
                disabled={loading}
              />
              {errors.reason && (
                <p id="movement-reason-error" role="alert" className="text-danger-600 text-xs mt-1">{errors.reason}</p>
              )}
            </div>

            <div>
              <label htmlFor="movement-notes" className="block text-sm font-medium text-secondary-700 mb-1">
                หมายเหตุเพิ่มเติม
              </label>
              <textarea
                id="movement-notes"
                maxLength={2000}
                name="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="input"
                placeholder="หมายเหตุเพิ่มเติม (ถ้ามี)"
                disabled={loading}
              />
            </div>

            {/* ปิดโครงการเดิม */}
            {canDeactivateOldSite ? (
              <label className="flex items-start gap-2 bg-warning-50 border border-warning-200 rounded-lg p-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={deactivateOldSite}
                  onChange={(e) => setDeactivateOldSite(e.target.checked)}
                  className="w-4 h-4 mt-0.5 text-warning-600 focus:ring-warning-500 rounded"
                  disabled={loading}
                />
                <span className="text-sm text-warning-800">
                  ปิดโครงการเดิม &quot;{context.old_site.name}&quot; (ไม่ใช้งาน)
                  <span className="block text-xs text-warning-700 mt-0.5">
                    โครงการเดิมไม่เหลือการเช่า/สัญญาอื่นแล้ว — เปิดใช้งานใหม่ได้ภายหลังจากหน้าข้อมูลโครงการ
                  </span>
                </span>
              </label>
            ) : (
              <div className="flex items-start gap-2 bg-secondary-50 border border-secondary-200 rounded-lg p-3">
                <RiInformationLine className="w-5 h-5 text-secondary-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-secondary-600">
                  {isPartialMove ? (
                    <>
                      ย้ายบางรายการ — สัญญาเดิมยังอยู่ที่โครงการ
                      &quot;{context.old_site.name}&quot; จึงปิดโครงการเดิมไม่ได้
                    </>
                  ) : (
                    <>
                      โครงการเดิม &quot;{context.old_site.name}&quot; ยังมีการเช่า/สัญญาอื่น{' '}
                      {blockingCount} รายการ จะไม่ถูกปิด
                    </>
                  )}
                </p>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {/* Footer */}
      <div className="mt-6 flex justify-end gap-3">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-md hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          disabled={loading}
        >
          ยกเลิก
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          disabled={loading || loadingContext || !context}
        >
          {loading && (
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          )}
          <RiTruckLine className="w-4 h-4 mr-1" />
          {isPartialMove ? `ย้ายรายการที่เลือก (${selectedCount})` : 'ย้ายอุปกรณ์'}
        </button>
      </div>
    </Modal>
  );
};

export default DeviceMovementModal;
